<template>
  <article ref="drillDataPopupContainerRef" class="relative drill-data-popup-container">
    <slot name="button" />

    <FloatingMenu v-if="showing" class="mt-4 drill-data-popup min-w-128" arrow-position="none">
      <VSearchable
        placeholder="Search by drill data name or asset"
        :search="search"
        :loading="loading"
        :count="count"
        :auto-load="true"
        :page-count="drillData.length"
        :has-gap="false"
        @update:search="searchDrillData"
        @load-more="scroll"
      >
        <template #no-results>
          <article data-testid="drill-data-no-result" class="flex flex-col justify-center max-w-xs mx-auto text-center text-gray-700 h-76">
            <p class="text-lg">No Drill Data Found!</p>
            <p class="m-0">Please upload drill data from the data file manager to add it to your model</p>
          </article>
        </template>

        <EditorDrillDataTile
          v-for="data in drillData"
          :key="data.id"
          :data="data"
          :selected="selectedData?.id === data.id"
          :is-added-to-model="dataSetIdsAdded.includes(data.id)"
          @selected="onDataSelected"
        />
      </VSearchable>

      <div class="px-3 pt-1 pb-4">
        <VButton
          v-if="!drillData.length"
          data-testid="drill-data-file-manager-link"
          class="flex items-center justify-center w-full"
          component="a"
          :href="drillDataManagerEndpoint"
          target="_blank"
          size="sm"
          variant="black"
        >
          <ArrowTopRightOnSquareIcon class="w-5 mr-2" />
          <span>Go to data file manager</span>
        </VButton>
        <slot v-else name="footer" />
      </div>
    </FloatingMenu>
  </article>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { ref, computed, watch } from 'vue'
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline'

import type { Drill } from '@/entities/drill'
import type { QueryParameters } from '@/composables/useIndexSearch'
import type { DrillDataSetManager } from '~/lib/three-d-resources/drill-data-set-manager'
import type { EditDeck } from '~/composables/useEditDeck'
import { debounce } from '@/lib/utils'

interface SortQuery extends QueryParameters {
  sortKey: 'name' | 'updated_at'
  sortDir: '-' | ''
}
interface ApiQuery {
  query: string | null
  limit: number
}

const props = defineProps({
  showing: {
    type: Boolean,
    default: false,
  },
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
  drillDataSetManager: {
    type: Object as PropType<DrillDataSetManager>,
    required: true,
  },
})

const emit = defineEmits(['selected', 'close'])

const selectedData = ref<Drill | null>(null)
const drillDataPopupContainerRef = ref<any>(null)
const dataSetIdsAdded = ref<number[]>([])
const config = useRuntimeConfig()
const {
  results: data,
  query: search,
  count,
  continuation,
  loading,
  refresh,
} = useIndexSearch<Drill, SortQuery, ApiQuery>({
  initParams: { limit: 12, sortDir: '-', sortKey: 'updated_at' },
  endpoint: () => `v2/drill/companies/${props.deck.company_id}/data`,
  finalParameters: ({ query, limit, sortKey, sortDir }) => ({
    sort_by: `${sortDir}${sortKey}`,
    query,
    limit,
  }),
})

const drillDataManagerEndpoint = computed(() => `${config.public.ADMIN_ENDPOINT}/data-files`)

const drillData = computed(() => {
  if (props.deck.view_access_type === 'public') {
    return data.value.filter((data: Drill) => data.view_access_type === 'public')
  }
  return data.value
})

useClickOutside([computed(() => drillDataPopupContainerRef.value)], ref(false), () => emit('close'))

watch(
  () => props.showing,
  () => {
    if(props.showing) {
      selectedData.value = null
      dataSetIdsAdded.value = props.drillDataSetManager.drillDataSets.map(dataSet => dataSet.data_set_id)
      refresh()
    }
  }
)

function onDataSelected(data: Drill) {
  selectedData.value = data
  emit('selected', data)
}

async function scroll() {
  if (!loading.value && count.value > data.value.length) {
    continuation.value++
  }
}

function _searchDrillData(query: string) {
  search.value = query
}

const searchDrillData = debounce(_searchDrillData, 500)
</script>

<style lang="stylus" scoped>
.drill-data-popup
  @apply absolute z-20
</style>
